import React, { useEffect, useState } from "react";
import { ResourceBookingSchema, SearchFilterOptionType } from "../../../data/types";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import Pagination from "../../../components/Desktop/Pagination";
import { usePagination } from "../../../hooks/paginationhooks";

import BookedResourceCard from "../../../components/Desktop/BookedResourceCard";
import BookedResourceListRow from "../../../components/Desktop/BookedResourceListRow";
import BookingsCalendarView from "../../../components/Desktop/BookingsCalendarView";
import BookingViewControls from "../../../components/Desktop/BookingViewControls";
import {
  useCompleteSignIn,
  useLoadBookings,
} from "../../../hooks/authhooks";
import { useNavigate } from "react-router-dom";
import { useBookingsFilterSort } from "../../../hooks/apphooks";
import { BookingDetailsModal, DeleteConfirmationModal } from '../../../molecules/Desktop/AppModal';
import MyBookingsSkeleton from "../../../components/MyBookingsSkeleton";
import { Snackbar, Alert } from "@mui/material";
import { toast } from "sonner";


type SavedResourceListingProps = {
  searchFilterOptions: SearchFilterOptionType
}

const MyBookings: React.FC<SavedResourceListingProps> = ({ searchFilterOptions }) => {
  const { user } = useCompleteSignIn();
  const navigate = useNavigate();
  const { searchTerm } = searchFilterOptions;

  const [width, setWidth] = useState(window.innerWidth);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }
  useEffect(() => {
    document.title = `HomeCook | My Bookings`;
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const isMobile = width < 1024;

  const loggedinUid = user?.uid ? user?.uid : "";
  const { bookings, updateBooking, cancelBooking, isLoading, isUpdateBookingSuccess, isUpdateBookingError } = useLoadBookings(loggedinUid);
  const allBookings = bookings ? bookings : [];

  const {
    viewMode,
    setViewMode,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    filters,
    setFilters,
    showFilters,
    setShowFilters,
    processedBookings,
    activeFilterCount,
    resetFilters,
  } = useBookingsFilterSort(allBookings, searchTerm);

  const {
    currentPage: bookingsCurrentPage,
    setCurrentPage: setBookingsCurrentPage,
    paginatedItems: paginatedBookings,
    startIndex: bookingsStartIndex,
    endIndex: bookingsEndIndex,
    totalItems: numBookings,
    itemsPerPage: bookingsPerPage
  } = usePagination(processedBookings);

  useEffect(() => {
    setBookingsCurrentPage(1);
  }, [viewMode, sortField, sortDirection, filters, searchTerm, setBookingsCurrentPage]);

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<ResourceBookingSchema | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleViewDetails = (booking: ResourceBookingSchema) => {
    setSelectedBooking(booking);
    setIsEditMode(false);
    setIsBookingModalOpen(true);
  };

  const handleEditDetails = (booking: ResourceBookingSchema) => {
    setSelectedBooking(booking);
    setIsEditMode(true);
    setIsBookingModalOpen(true);
  };

  const handleCloseBookingModal = () => {
    setIsBookingModalOpen(false);
    setSelectedBooking(null);
    setIsEditMode(false);
  };

  const handleSaveBookingChanges = (communityId: string, bookingId: string, changedAttributes: ResourceBookingSchema) => {
    if (bookingId && communityId && changedAttributes){
      updateBooking(communityId, bookingId, changedAttributes);
      setIsBookingModalOpen(false);
    }
  };

  const handleCancelBooking = async (communityId: string, resourceId: string, bookingId: string) => {
    if (bookingId && communityId && resourceId){
      const res = await cancelBooking(communityId, resourceId, bookingId);
      if (res){
        toast.success("Booking cancelled successfully", {position: "top-right", className: "bg-green-500 text-white", icon: "🎉"});
      } else {
        toast.error("Failed to cancel booking. Please try again.", {position: "top-right", className: "bg-red-500 text-white", icon: "🚫"});
      }
      setIsDeleteConfirmationModalOpen(false);
      setIsBookingModalOpen(false);
    }
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success");

  useEffect(() => {
    if (isUpdateBookingSuccess) {
      setSnackbarMessage("Booking updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } else if (isUpdateBookingError) {
      setSnackbarMessage("Failed to update booking. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [isUpdateBookingSuccess, isUpdateBookingError]);

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  if (isLoading){
    return <MyBookingsSkeleton variant="desktop" />
  }

  const displayBookings = viewMode === 'calendar' ? processedBookings : paginatedBookings;
  const showPagination = viewMode !== 'calendar';

  return (
      <>
        <div
          className={`w-full left-0 flex font-open-sans h-full overflow-y-scroll relative top-0 justify-center bg-gradient-to-b from-[#F5F5F5] pb-6 pt-8 backdrop-blur-2xl bg-[#fff]`}
        >
          <div
            className={`flex flex-col w-11/12 sm:w-10/12 min-h-full relative items-start py-6 bg-white`}
          >
            <div className="flex flex-col mx-auto w-full sm:w-[1200px] bg-[#FFFFFF] rounded-xl mt-10 pb-10">
              <span
                className="font-semibold text-[14px] m-[24px] text-[#8A8A8A] cursor-pointer flex flex-row font-raleway"
                onClick={() => navigate(-1)}
              >
                <ArrowBackIosIcon style={{fontSize: 14}} className="my-auto" />
                <span className="my-auto ml-1 font-semibold">Go Back</span>
              </span>
              <div className="flex flex-row w-full">
                <h1 className="font-semibold text-2xl text-[#013847] pl-6 mr-auto font-raleway">
                  My Bookings and Reservations
                </h1>
              </div>

              <BookingViewControls
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                sortField={sortField}
                onSortFieldChange={setSortField}
                sortDirection={sortDirection}
                onSortDirectionChange={setSortDirection}
                filters={filters}
                onFiltersChange={setFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters((prev) => !prev)}
                activeFilterCount={activeFilterCount}
                onResetFilters={resetFilters}
                resultCount={processedBookings.length}
              />

              {showPagination && (
                <Pagination
                  currentPage={bookingsCurrentPage}
                  totalItems={numBookings}
                  startIndex={bookingsStartIndex}
                  endIndex={bookingsEndIndex}
                  onPageChange={setBookingsCurrentPage}
                  itemsPerPage={bookingsPerPage}
                />
              )}

              {viewMode === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mx-auto w-full sm:w-[1200px] bg-[#fff] rounded-xl px-6">
                  {displayBookings.length > 0 ? (
                    displayBookings.map((booking: ResourceBookingSchema, i: number) => (
                      <BookedResourceCard
                        key={booking.id || i}
                        isMobile={isMobile}
                        booking={booking}
                        onViewDetails={handleViewDetails}
                        onEditDetails={handleEditDetails}
                      />
                    ))
                  ) : (
                    <div className="flex flex-row mx-auto justify-evenly font-light text-center text-[#013847]/60 text-xs sm:text-sm col-span-full py-8">
                      No booked resources to show
                    </div>
                  )}
                </div>
              )}

              {viewMode === 'list' && (
                <div className="flex flex-col gap-3 mx-auto w-full sm:w-[1200px] px-6">
                  {displayBookings.length > 0 ? (
                    displayBookings.map((booking: ResourceBookingSchema, i: number) => (
                      <BookedResourceListRow
                        key={booking.id || i}
                        booking={booking}
                        onViewDetails={handleViewDetails}
                        onEditDetails={handleEditDetails}
                      />
                    ))
                  ) : (
                    <div className="flex flex-row mx-auto justify-center font-light text-center text-[#013847]/60 text-sm py-8">
                      No booked resources to show
                    </div>
                  )}
                </div>
              )}

              {viewMode === 'calendar' && (
                <BookingsCalendarView
                  bookings={processedBookings}
                  onViewDetails={handleViewDetails}
                />
              )}
            </div>
          </div>
        </div>
        <BookingDetailsModal
          userBookings={allBookings}
          booking={selectedBooking}
          isOpen={isBookingModalOpen}
          isEditMode={isEditMode}
          onClose={handleCloseBookingModal}
          onCancel={()=>setIsDeleteConfirmationModalOpen(true)}
          onSave={handleSaveBookingChanges}
          searchFilterOptions={searchFilterOptions}
        />
        <DeleteConfirmationModal
          isOpen={isDeleteConfirmationModalOpen}
          closeDeleteModal={()=>setIsDeleteConfirmationModalOpen(false)}
          handleDelete={(dataId: string)=>handleCancelBooking(selectedBooking?.community_id || "", selectedBooking?.resource_id || "", dataId)}
          dataName="reservation"
          data={selectedBooking ? {
            ...selectedBooking,
            id: selectedBooking.id,
            item_name: selectedBooking.summary,
            date: selectedBooking.start_time
          } : null}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </>
    );
};

export default MyBookings;
